import Median from 'median-js-bridge';
import { isMedianApp } from './medianOneSignal';

/**
 * Haptic (vibration) feedback through the Median JavaScript Bridge.
 *
 * These calls ONLY do something inside the Median native app wrapper, where the
 * Haptics plugin is enabled from the Median dashboard. In a regular browser or
 * the installed PWA every call here is a no-op, and it never throws.
 */
export type HapticStyle =
  | 'impactLight'
  | 'impactMedium'
  | 'impactHeavy'
  | 'notificationSuccess'
  | 'notificationWarning'
  | 'notificationError';

/**
 * True when the native message channel the app uses to receive commands is
 * actually present. Inside the real app this is there; in a browser it is not.
 */
export const hasNativeBridge = (): boolean => {
  if (typeof window === 'undefined') return false;
  const w = window as any;
  return !!(w?.JSBridge?.postMessage || w?.webkit?.messageHandlers?.JSBridge?.postMessage);
};

/**
 * Returns true when the vibration command was dispatched to the native bridge,
 * false when there is nothing to dispatch to (web, PWA) or the call failed.
 */
export const haptic = (style: HapticStyle = 'impactLight'): boolean => {
  if (typeof window === 'undefined') return false;
  if (!isMedianApp()) return false;
  if (!hasNativeBridge()) return false;
  try {
    (Median as any)?.haptics?.trigger?.({ style });
    console.log('[haptics] trigger', style);
    return true;
  } catch (e) {
    console.log('[haptics] skipped', e);
    return false;
  }
};


export const hapticTap = () => haptic('impactLight');
export const hapticSuccess = () => haptic('notificationSuccess');
export const hapticError = () => haptic('notificationError');

/**
 * Median implements pull-to-refresh in the native wrapper and does not expose
 * a JavaScript gesture callback. The reliable observable point is therefore
 * the new page load after the native control refreshes the WebView.
 */

/** Small observable state so Settings can show what actually happened. */
export const pullToRefreshHapticState: {
  refreshDetected: boolean;
  hapticSentAt: number | null;
} = {
  refreshDetected: false,
  hapticSentAt: null,
};

const refreshStateListeners = new Set<() => void>();
let refreshHapticRegistered = false;

const notifyRefreshStateListeners = () => {
  refreshStateListeners.forEach((listener) => listener());
};

export const subscribeToPullToRefreshHapticState = (listener: () => void) => {
  refreshStateListeners.add(listener);
  return () => {
    refreshStateListeners.delete(listener);
  };
};

const wasPageReloaded = (): boolean => {
  if (typeof window === 'undefined') return false;

  const navigationEntry = window.performance
    ?.getEntriesByType('navigation')
    .find((entry): entry is PerformanceNavigationTiming => 'type' in entry);

  if (navigationEntry?.type === 'reload') return true;

  // Older native WebViews may only expose the deprecated navigation object.
  return window.performance?.navigation?.type === 1;
};

export const registerPullToRefreshHaptic = () => {
  if (typeof window === 'undefined') return;
  if (refreshHapticRegistered) return;
  refreshHapticRegistered = true;

  pullToRefreshHapticState.refreshDetected = wasPageReloaded();
  notifyRefreshStateListeners();
  if (!pullToRefreshHapticState.refreshDetected) return;

  const sendRefreshHaptic = () => {
    if (pullToRefreshHapticState.hapticSentAt !== null) return;
    if (!hapticTap()) return;

    pullToRefreshHapticState.hapticSentAt = Date.now();
    notifyRefreshStateListeners();
    console.log('[haptics] refresh completion confirmed');
  };

  try {
    Median.onReady(sendRefreshHaptic);
  } catch (error) {
    console.log('[haptics] refresh confirmation skipped', error);
  }
};
